/**
 * Utility for sending Slack webhook notifications
 */

/**
 * Send a notification to Slack
 * @param {Object} data - The data to send to Slack
 * @param {string} type - The type of notification (contact, deletion, or consultation)
 * @returns {Promise} - Promise resolving to the fetch response
 */
export const sendSlackNotification = async (data, type) => {
  try {
    // Format message will remain on the client
    let message = '';
    
    if (type === 'contact') {
      message = {
        blocks: [
          {
            type: "header",
            text: {
              type: "plain_text",
              text: "📬 New Contact Form Submission",
              emoji: true
            }
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Name:*\n${data.name}`
              },
              {
                type: "mrkdwn",
                text: `*Email:*\n${data.email}`
              }
            ]
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Company:*\n${data.companyName || '-'}`
              },
              {
                type: "mrkdwn",
                text: `*Phone:*\n${data.phone || '-'}`
              }
            ]
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Subject:*\n${data.subject}`
              }
            ]
          },
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*Message:*\n${data.message}`
            }
          },
          {
            type: "context",
            elements: [
              {
                type: "plain_text",
                text: `Submitted on ${new Date().toLocaleString()}`,
                emoji: true
              }
            ]
          }
        ]
      };
    } else if (type === 'deletion') {
      message = {
        blocks: [
          {
            type: "header",
            text: {
              type: "plain_text",
              text: "🚨 Account Deletion Request",
              emoji: true
            }
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Email:*\n${data.email}`
              },
              {
                type: "mrkdwn",
                text: `*Company:*\n${data.companyName}`
              }
            ]
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Phone:*\n${data.phone}`
              },
              {
                type: "mrkdwn",
                text: `*Reason:*\n${data.reason}`
              }
            ]
          },
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*Additional Information:*\n${data.additionalInfo || 'None provided'}`
            }
          },
          {
            type: "context",
            elements: [
              {
                type: "plain_text",
                text: `Requested on ${new Date().toLocaleString()}`,
                emoji: true
              }
            ]
          }
        ]
      };
    } else if (type === 'consultation') {
      // New message format for consultation requests
      message = {
        blocks: [
          {
            type: "header",
            text: {
              type: "plain_text",
              text: `🔔 New ${data.requestType === "demo" ? "Demo" : 
                     data.requestType === "consultation" ? "Consultation" : 
                     "Information"} Request`,
              emoji: true
            }
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Name:*\n${data.name}`
              },
              {
                type: "mrkdwn",
                text: `*Company:*\n${data.companyName}`
              }
            ]
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Email:*\n${data.email}`
              },
              {
                type: "mrkdwn",
                text: `*Phone:*\n${data.phone || 'Not provided'}`
              }
            ]
          },
          {
            type: "section",
            fields: [
              {
                type: "mrkdwn",
                text: `*Subject:*\n${data.subject || 'Not specified'}`
              }
            ]
          },
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*Message:*\n${data.message || 'None provided'}`
            }
          },
          {
            type: "context",
            elements: [
              {
                type: "plain_text",
                text: `Submitted on ${new Date().toLocaleString()}`,
                emoji: true
              }
            ]
          }
        ]
      };
    } else if (type === 'waitlist') {
      message = {
        blocks: [
          {
            type: "header",
            text: {
              type: "plain_text",
              text: `🗓️ Pre-registration: ${data.product}`,
              emoji: true
            }
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: `*Email:*\n${data.email}` },
              { type: "mrkdwn", text: `*Company:*\n${data.companyName || '-'}` }
            ]
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: `*Product:*\n${data.product}` },
              { type: "mrkdwn", text: `*Source:*\n${data.source || 'bloomnav.com/products'}` }
            ]
          },
          {
            type: "context",
            elements: [
              { type: "plain_text", text: `Submitted on ${new Date().toLocaleString()}`, emoji: true }
            ]
          }
        ]
      };
    } else if (type === 'pilot') {
      message = {
        blocks: [
          {
            type: "header",
            text: {
              type: "plain_text",
              text: "New Pilot Onboarding Application",
              emoji: true
            }
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: `*Name:*\n${data.name}` },
              { type: "mrkdwn", text: `*Email:*\n${data.email}` }
            ]
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: `*Phone:*\n${data.phone}` },
              { type: "mrkdwn", text: `*Company:*\n${data.companyName}` }
            ]
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: `*Employees:*\n${data.numEmployees}` }
            ]
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: `*Business Type:*\n${data.businessType}` }
            ]
          },
          {
            type: "section",
            fields: [
              { type: "mrkdwn", text: `*Problem Description:*\n${data.problemDescription}` }
            ]
          },
          {
            type: "context",
            elements: [
              { type: "plain_text", text: `Submitted on ${new Date().toLocaleString()}`, emoji: true }
            ]
          }
        ]
      };
    }

    // Send to your own backend API instead of directly to Slack
    const response = await fetch('/api/notifications/slack', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message, type }),
    });

    if (!response.ok) {
      throw new Error(`Error sending Slack notification: ${response.statusText}`);
    }

    return response;
  } catch (error) {
    console.error('Failed to send Slack notification:', error);
    // Don't rethrow the error to prevent breaking the user experience
  }
}; 